/**
 * Gaussian random-walk price series, k-rescaled to realise ret[w] (plan §4.3).
 * Final bar equals `price` exactly by construction (pinned after IEEE walk).
 */
import type { WindowKey } from '@meridian/contracts';

import { SERIES_RET_FLOOR } from '../constants/generation';
import { WN, WYR } from '../constants/windows';
import { fnv1a } from '../rand/fnv1a';
import { gauss } from '../rand/gauss';
import { createRng } from '../rand/mulberry32';
import { seedPriceSeries } from '../rand/seed';

import { seriesCacheKey, type SeriesCache } from './series-cache';

import type { DerivedSecurity } from '../universe/derive-security';

/* Imperative walk mirrors the demo; loops + local mutation stay inside this
   module. The series cache Map is the only cross-call sanctioned mutation. */
/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */

function walkSeries(security: DerivedSecurity, window: WindowKey): readonly number[] {
  const n = WN[window];
  const R = createRng(fnv1a(seedPriceSeries(security.ticker, window)));
  const sv = security.vol * Math.sqrt(WYR[window] / n);
  const arr: number[] = [1];
  let p = 1;
  for (let i = 1; i < n; i++) {
    p *= 1 + sv * gauss(R);
    arr.push(p);
  }
  const periodRet = security.ret[window];
  const last = arr[n - 1];
  if (last === undefined) {
    throw new Error(`series walk empty for n=${String(n)}`);
  }
  const target = Math.max(SERIES_RET_FLOOR, 1 + periodRet);
  const k = Math.pow(target / last, 1 / (n - 1));
  const price = security.price;
  const out = arr.map((v, i) => (v * Math.pow(k, i) * price) / target);
  /* Algebra telescopes to price; pin final bar for plan §4.3 exactness.
     This is a DELIBERATE ~1e-14 departure from demos/Meridian Terminal.dc.html
     line 715, which has no pin and so carries IEEE residue in its last bar
     (AAPL/1M: 236.89000000000013 vs 236.89 here). Plan §4.3 requires
     `series(t,w)[n-1] === price` exactly, which the pure algebra cannot satisfy;
     the plan wins on conflict. Every interior bar is bit-identical to the demo.
     series.test.ts asserts the pin stays within 1e-12 of the un-pinned value, so
     a refactor that actually breaks the telescoping is still caught. */
  out[n - 1] = price;
  return Object.freeze(out);
}

/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */

/**
 * Generate adjusted-close bars for (security, window).
 * Optional cache is the only sanctioned memo — cold deep-equals warm.
 */
export function generateSeries(
  security: DerivedSecurity,
  window: WindowKey,
  cache?: SeriesCache,
): readonly number[] {
  const key = seriesCacheKey({
    ticker: security.ticker,
    window,
    price: security.price,
    vol: security.vol,
    periodRet: security.ret[window],
  });
  if (cache !== undefined) {
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
  }
  const bars = walkSeries(security, window);
  if (cache !== undefined) {
    cache.set(key, bars);
  }
  return bars;
}
